<template>
  <div class="cg-page">
    <div class="info-bar">
      左侧按国家维护客户组：每个国家可指定一个默认分组，注册时未指定分组的客户落在该国的默认分组上；客户分组决定 KYC 要求和档位上限。
    </div>

    <div class="cg-layout">
      <!-- ============ 左侧国家列表 ============ -->
      <div class="cg-sidebar">
        <div class="sidebar-add-area">
          <el-button
            v-auth="'business:customerGroup:save'"
            v-if="!showCountrySelect"
            type="primary"
            size="small"
            icon="el-icon-plus"
            style="width:100%;"
            @click="openCountrySelect"
          >添加国家配置</el-button>
          <div v-else class="select-with-close">
            <el-select
              ref="countrySelect"
              v-model="addingCountry"
              filterable
              placeholder="搜索国家..."
              popper-class="country-select-popper"
              size="small"
              style="flex:1;"
              @change="onCountrySelect"
            >
              <el-option
                v-for="item in availableCountryOptions"
                :key="item.countryCode"
                :label="countryOptionLabel(item)"
                :value="item.countryCode"
              />
            </el-select>
            <i class="el-icon-close close-select-btn" @click="onCountryClear"></i>
          </div>
        </div>

        <div class="country-list">
          <div
            class="country-item"
            :class="{ active: currentCountry === 'default' }"
            @click="switchCountry('default')"
          >
            <span class="country-icon">⚙️</span>
            <span class="country-name">默认配置</span>
          </div>
          <div
            v-for="item in sidebarCountries"
            :key="item.countryCode"
            class="country-item"
            :class="{ active: currentCountry === item.countryCode }"
            @click="switchCountry(item.countryCode)"
          >
            <span class="country-icon">📍</span>
            <span class="country-name">{{ countryOptionLabel(item) }}</span>
            <i
              v-auth="'business:customerGroup:delete'"
              class="el-icon-delete country-delete"
              title="删除该国家的全部客户组配置"
              @click.stop="handleCountryMenu('remove', item)"
            ></i>
          </div>
        </div>
      </div>

      <!-- ============ 右侧内容区 ============ -->
      <div class="cg-content" v-loading="loading">
        <div class="content-header">
          <div class="content-title-row">
            <div class="content-title">{{ currentCountryTitle }}</div>
            <el-tag size="mini" effect="plain" class="country-type-tag">{{ currentCountryTag }}</el-tag>
          </div>
        </div>

        <div class="table-wrapper">
          <div class="table-header">
            <span class="table-title">客户组列表（共 {{ countryGroups.length }} 个）</span>
            <el-button v-auth="'business:customerGroup:save'" type="primary" size="small" icon="el-icon-plus" @click="openAdd">新增客户组</el-button>
          </div>

          <el-table :data="countryGroups" border style="width:100%;" class="cg-table">
            <el-table-column type="index" label="#" width="50" align="center"></el-table-column>
            <el-table-column label="组别ID" width="100">
              <template slot-scope="scope"><span class="mono">{{ scope.row.id }}</span></template>
            </el-table-column>
            <el-table-column prop="groupName" label="组别名称" width="140"></el-table-column>
            <el-table-column label="描述" min-width="200" show-overflow-tooltip>
              <template slot-scope="scope">
                <span class="cell-muted">{{ scope.row.description || '-' }}</span>
              </template>
            </el-table-column>
            <el-table-column label="用户数量" width="100" align="center">
              <template slot-scope="scope"><span class="mono">{{ scope.row.userCount || 0 }}</span></template>
            </el-table-column>
            <el-table-column label="状态" width="80" align="center">
              <template slot-scope="scope">
                <el-switch
                  v-auth="'business:customerGroup:status'"
                  :value="scope.row.isDelete === 1"
                  @change="toggleStatus(scope.row)"
                  active-color="var(--brand-primary)"
                ></el-switch>
              </template>
            </el-table-column>
            <el-table-column label="设为默认" width="88" align="center">
              <template slot-scope="scope">
                <el-radio
                  v-auth="'business:customerGroup:edit'"
                  v-model="defaultGroupId"
                  :label="scope.row.id"
                  class="default-radio"
                  title="设为默认分组"
                />
              </template>
            </el-table-column>
            <el-table-column label="操作" width="130" fixed="right" align="center">
              <template slot-scope="scope">
                <el-button v-auth="'business:customerGroup:edit'" type="text" size="small" @click="openEdit(scope.row)">编辑</el-button>
                <el-button v-auth="'business:customerGroup:delete'" type="text" size="small" style="color:#f56c6c;" @click="deleteRow(scope.row)">删除</el-button>
              </template>
            </el-table-column>
            <template slot="empty">
              <div class="empty-tip">暂无客户组，点击右上角「新增客户组」添加</div>
            </template>
          </el-table>
        </div>
      </div>
    </div>

    <!-- ============ 新增/编辑客户组弹窗 ============ -->
    <el-dialog :title="dialogTitle" :visible.sync="dialogVisible" width="720px" :close-on-click-modal="false">
      <el-form label-position="top" size="small">
        <!-- 国家：只读置顶，值跟随左侧选中的国家 -->
        <el-form-item label="国家">
          <el-input :value="formCountryLabel" disabled></el-input>
        </el-form-item>

        <div style="display:grid;grid-template-columns:1fr 1fr;gap:16px;">
          <el-form-item label="组别名称" required>
            <el-input v-model="form.groupName" placeholder="如：标准组"></el-input>
          </el-form-item>
          <el-form-item label="说明">
            <el-input v-model="form.description" placeholder="组别说明"></el-input>
          </el-form-item>
        </div>

        <!-- 权限设置 -->
        <div style="margin-top:16px;padding-top:16px;border-top:1px solid #ebeef5;">
          <div style="font-size:14px;font-weight:600;margin-bottom:12px;color:#303133;">权限设置</div>
          <div style="overflow-x:auto;">
            <table style="width:100%;border-collapse:collapse;font-size:13px;border:1px solid #ebeef5;">
              <thead>
                <tr style="background:#f5f7fa;">
                  <th style="padding:8px 12px;text-align:left;font-weight:500;color:#909399;border-bottom:1px solid #ebeef5;">权限类型</th>
                  <th style="padding:8px 12px;text-align:center;font-weight:500;color:#909399;border-bottom:1px solid #ebeef5;width:80px;">启用状态</th>
                  <th style="padding:8px 12px;text-align:left;font-weight:500;color:#909399;border-bottom:1px solid #ebeef5;">KYC认证</th>
                  <th style="padding:8px 12px;text-align:left;font-weight:500;color:#909399;border-bottom:1px solid #ebeef5;width:100px;">操作人员</th>
                  <th style="padding:8px 12px;text-align:left;font-weight:500;color:#909399;border-bottom:1px solid #ebeef5;width:140px;">操作时间</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(p, idx) in form.permissions" :key="idx" style="border-bottom:1px solid #ebeef5;">
                  <td style="padding:8px 12px;">{{ permTypeLabel(p.permType) }}</td>
                  <td style="padding:8px 12px;text-align:center;">
                    <el-switch v-model="p.isDelete" :active-value="1" :inactive-value="2" active-color="var(--brand-primary)"></el-switch>
                  </td>
                  <td style="padding:8px 12px;">
                    <!-- KYC 等级只由下拉框自身的选中项表达，不在框上方重复展示标签。
                         开立真实账户(perm_type=5) 固定为「身份认证」，下拉禁用不可改 -->
                    <el-select
                      v-model="p.kycLevel"
                      multiple
                      size="mini"
                      style="width:220px;"
                      placeholder="请选择KYC等级"
                      :disabled="isKycLocked(p)"
                      :title="isKycLocked(p) ? '开立真实账户固定要求身份认证，不可修改' : ''"
                    >
                      <el-option v-for="k in kycOptions" :key="k.code" :label="k.desc" :value="k.code"></el-option>
                    </el-select>
                  </td>
                  <td style="padding:8px 12px;">{{ p.createUser || '-' }}</td>
                  <td style="padding:8px 12px;">{{ p.createDate || '-' }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <el-form-item style="margin:16px 0 0;">
          <el-checkbox v-model="formIsDefault">设为默认分组</el-checkbox>
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button size="small" @click="dialogVisible = false">取消</el-button>
        <el-button size="small" type="primary" @click="save">确定</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import {
  customerGroupList, customerGroupAddOrUpdate, customerGroupDeleteBatchIds,
  customerGroupStatus, customerGroupPermissions, customerGroupSetDefault
} from '@/api/customerGroup'
import { countryList } from '@/api/regConfig'

/** 未单独配置客户组的国家，注册时回退到这个国家的分组 */
const DEFAULT_COUNTRY = 'DEFAULT'

// 新建客户组的默认权限：入金要求身份认证、出金要求绑定银行卡，内部转账/启用交易默认无 KYC 要求，
// 开立真实账户固定要求身份认证（该行 KYC 不可修改，见 LOCKED_KYC_PERM_TYPES）
const DEFAULT_PERMS = [
  { permType: 1, kycLevel: [2], isDelete: 1, sort: 1 },
  { permType: 2, kycLevel: [6], isDelete: 1, sort: 2 },
  { permType: 3, kycLevel: [], isDelete: 1, sort: 3 },
  { permType: 4, kycLevel: [], isDelete: 1, sort: 4 },
  { permType: 5, kycLevel: [2], isDelete: 1, sort: 5 }
]

const PERM_TYPE_LABELS = { 1: '入金', 2: '出金', 3: '内部转账', 4: '启用交易', 5: '开立真实账户' }

// KYC 等级写死、下拉框不可修改的权限类型：开立真实账户(5) 必须是「身份认证」(2)。
// 只有「启用状态」可切换 —— C 端据此判断能否开立真实账户。
const LOCKED_KYC_LEVEL = [2]
const LOCKED_KYC_PERM_TYPES = [5]

// KYC 等级下拉项：不含历史遗留的 1「添加」（无实际认证含义，前端不再可选）
const KYC_OPTIONS = [
  { code: 2, desc: '身份认证' },
  { code: 3, desc: '绑定手机号' }, { code: 4, desc: '绑定邮箱' },
  { code: 5, desc: '绑定电子钱包' }, { code: 6, desc: '绑定银行卡' },
  { code: 7, desc: '设置资金密码' }
]

function emptyForm() {
  return {
    id: null, groupName: '', countryCode: DEFAULT_COUNTRY, description: '',
    // kycLevel 深拷贝：多选下拉会改动数组本身，浅拷贝会污染 DEFAULT_PERMS
    isDefault: false, permissions: DEFAULT_PERMS.map(p => ({ ...p, kycLevel: p.kycLevel.slice() }))
  }
}

export default {
  name: 'CustomerGroupList',
  data() {
    return {
      loading: false,
      // 全量客户组：左侧国家列表与右侧表格都由它派生，避免两次请求算出不一致的视图
      allGroups: [],
      allCountries: [],
      currentCountry: 'default',
      showCountrySelect: false,
      addingCountry: null,
      dialogVisible: false, dialogTitle: '新增客户组', editing: false,
      form: emptyForm(),
      kycOptions: KYC_OPTIONS
    }
  },
  computed: {
    /** 左侧国家列表 = 已有客户组的国家（与开户配置/交易组别页的左侧列表同源逻辑） */
    sidebarCountries() {
      const seen = new Set()
      const list = []
      this.allGroups.forEach(g => {
        const code = g.countryCode
        if (!code || code === DEFAULT_COUNTRY || seen.has(code)) return
        seen.add(code)
        list.push({ countryCode: code })
      })
      return list.sort((a, b) => String(a.countryCode).localeCompare(String(b.countryCode)))
    },
    /** 「添加国家配置」下拉：排除已出现在左侧的国家 */
    availableCountryOptions() {
      return this.allCountries.filter(c =>
        c.countryCode !== DEFAULT_COUNTRY &&
        c.isDelete === 1 &&
        !this.sidebarCountries.some(s => s.countryCode === c.countryCode)
      )
    },
    /** 当前选中国家的客户组 */
    countryGroups() {
      const code = this.currentCountry === 'default' ? DEFAULT_COUNTRY : this.currentCountry
      return this.allGroups.filter(g => (g.countryCode || DEFAULT_COUNTRY) === code)
    },
    /** 「设为默认」列的单选：选中态由当前默认分组推导，点击只是设置动作的入口 */
    defaultGroupId: {
      get() {
        const current = this.countryGroups.find(g => g.isDefault === 1)
        return current ? current.id : null
      },
      set(id) {
        const row = this.countryGroups.find(g => g.id === id)
        if (row && row.isDefault !== 1) {
          this.setDefault(row)
        }
      }
    },
    currentCountryTitle() {
      return this.currentCountry === 'default'
        ? '默认配置'
        : `${this.currentCountry} - ${this.countryName(this.currentCountry)} 客户组`
    },
    currentCountryTag() {
      return this.currentCountry === 'default' ? '默认' : this.currentCountry
    },
    /** 弹窗里只读的「国家」文案 */
    formCountryLabel() {
      const code = this.form.countryCode
      if (!code || code === DEFAULT_COUNTRY) return '默认配置'
      return `${code} - ${this.countryName(code)}`
    },
    formIsDefault: {
      get() { return this.form.isDefault === true },
      set(v) { this.form.isDefault = v }
    }
  },
  mounted() {
    this.fetchCountries()
    this.fetchGroups()
  },
  methods: {
    fetchCountries() {
      countryList({ pageNum: 1, pageSize: 500 }).then(res => {
        if (res.code === 0) {
          this.allCountries = (res.result && res.result.list) || []
        }
      })
    },
    fetchGroups() {
      this.loading = true
      customerGroupList({ pageNum: 1, pageSize: 1000 }).then(res => {
        if (res.code === 0) {
          this.allGroups = (res.result && res.result.list) || []
          // 当前选中的国家被删光了 → 回到「默认配置」，避免停在空白面板
          if (this.currentCountry !== 'default' &&
            !this.allGroups.some(g => g.countryCode === this.currentCountry)) {
            this.currentCountry = 'default'
          }
        }
      }).finally(() => { this.loading = false })
    },

    // ==================== 国家列表 ====================
    countryName(code) {
      const c = this.allCountries.find(o => o.countryCode === code)
      return (c && (c.countryCname || c.countryName)) || code
    },
    countryOptionLabel(item) {
      const code = typeof item === 'string' ? item : (item && item.countryCode)
      if (!code || code === DEFAULT_COUNTRY) return '默认配置'
      return `${code} - ${this.countryName(code)}`
    },
    openCountrySelect() {
      this.showCountrySelect = true
      this.addingCountry = null
      this.$nextTick(() => {
        this.$refs.countrySelect && this.$refs.countrySelect.focus()
      })
    },
    onCountryClear() {
      this.showCountrySelect = false
      this.addingCountry = null
    },
    /** 添加国家配置：把「默认配置」的客户组（含权限）复制一份到该国家 */
    onCountrySelect(countryCode) {
      if (!countryCode) return
      this.showCountrySelect = false
      this.addingCountry = null
      const templates = this.allGroups.filter(g => (g.countryCode || DEFAULT_COUNTRY) === DEFAULT_COUNTRY)
      if (templates.length === 0) {
        this.currentCountry = countryCode
        this.$message.warning('「默认配置」下还没有客户组，已切换到该国家，请手动新增')
        return
      }
      this.loading = true
      Promise.all(templates.map(t => this.copyGroupToCountry(t, countryCode))).then(() => {
        this.$message.success(`已将「默认配置」的 ${templates.length} 个客户组复制到 ${this.countryName(countryCode)}`)
        // 复制完成后才切过去，避免刷新覆盖掉中途的选中态
        this.currentCountry = countryCode
      }).catch(() => {
        this.$message.error('复制客户组失败，请重试')
      }).finally(() => {
        this.loading = false
        this.fetchGroups()
      })
    },
    /** 复制单个客户组（连权限一起）到目标国家 */
    copyGroupToCountry(template, countryCode) {
      return customerGroupPermissions(template.id).then(res => {
        const perms = (res.code === 0 && res.result) ? res.result : []
        return customerGroupAddOrUpdate({
          countryCode,
          groupName: template.groupName,
          tier: template.tier,
          description: template.description,
          sort: template.sort,
          isDefault: template.isDefault === 1 ? 1 : 0,
          // 规整后补齐缺失的权限类型（老组没有「开立真实账户」行），KYC 回写逗号分隔字符串
          permissions: this.normalizePermissions(perms).map(p => ({
            permType: p.permType,
            kycLevel: p.kycLevel.join(','),
            sort: p.sort,
            isDelete: p.isDelete
          }))
        })
      })
    },
    switchCountry(countryCode) {
      this.currentCountry = countryCode
    },
    /** 删除国家 = 删掉该国的全部客户组，左侧随之消失 */
    handleCountryMenu(command, country) {
      if (command !== 'remove') return
      const rows = this.allGroups.filter(g => g.countryCode === country.countryCode)
      const name = this.countryOptionLabel(country)
      this.$confirm(`确定删除 ${name} 的全部客户组配置吗？（共 ${rows.length} 个客户组）`, '提示', { type: 'warning' }).then(() => {
        Promise.all(rows.map(r => customerGroupDeleteBatchIds(String(r.id)))).then(() => {
          this.$message.success('已删除')
          if (this.currentCountry === country.countryCode) {
            this.currentCountry = 'default'
          }
          this.fetchGroups()
        }).catch(() => {
          // 有客户组下还存在客户会被后端拒绝（失败原因由响应拦截器提示）：逐个删除会部分成功，
          // 这里同样刷新一次，避免已删掉的组还留在页面上
          this.fetchGroups()
        })
      }).catch(() => {})
    },

    // ==================== 客户组 ====================
    openAdd() {
      this.editing = false
      this.dialogTitle = '新增客户组'
      this.form = emptyForm()
      this.form.countryCode = this.currentCountry === 'default' ? DEFAULT_COUNTRY : this.currentCountry
      this.dialogVisible = true
    },
    openEdit(row) {
      this.editing = true
      this.dialogTitle = '编辑客户组 — ' + row.groupName
      this.form = {
        id: row.id, groupName: row.groupName,
        countryCode: row.countryCode || DEFAULT_COUNTRY,
        description: row.description,
        isDefault: row.isDefault === 1,
        permissions: DEFAULT_PERMS.map(p => ({ ...p, kycLevel: p.kycLevel.slice() }))
      }
      // 加载已有权限
      customerGroupPermissions(row.id).then(res => {
        const rows = (res.code === 0 && res.result)
          ? res.result.map(p => ({
            id: p.id, permType: p.permType, kycLevel: p.kycLevel,
            isDelete: p.isDelete, sort: p.sort, createDate: p.createDate, createUser: p.createUser
          }))
          : []
        // 规整：过滤已下线的 1「添加」等级、补齐历史客户组缺失的权限行（如 perm_type=5）、
        // 锁定类型的 KYC 固定为身份认证
        this.form.permissions = this.normalizePermissions(rows)
      })
      this.dialogVisible = true
    },
    save() {
      if (!this.form.groupName) { this.$message.warning('请输入组别名称'); return }
      const payload = {
        ...this.form,
        isDefault: this.form.isDefault ? 1 : 0,
        // 多选数组 → 逗号分隔字符串（后端存储格式）；
        // 锁定类型（开立真实账户）忽略界面值，固定为身份认证，后端也会再兜一层
        permissions: (this.form.permissions || []).map(p => ({
          ...p,
          kycLevel: (LOCKED_KYC_PERM_TYPES.indexOf(p.permType) !== -1 ? LOCKED_KYC_LEVEL : (p.kycLevel || [])).join(',')
        }))
      }
      customerGroupAddOrUpdate(payload).then(res => {
        if (res.code === 0) {
          this.$message.success(this.editing ? '编辑成功' : '新建成功')
          this.dialogVisible = false
          this.fetchGroups()
        }
      })
    },
    setDefault(row) {
      customerGroupSetDefault(row.id).then(res => {
        if (res.code === 0) {
          this.$message.success('已设为默认分组')
          this.fetchGroups()
        }
      })
    },
    deleteRow(row) {
      this.$confirm('确定删除该客户组吗？', '提示', { type: 'warning' }).then(() => {
        customerGroupDeleteBatchIds(String(row.id)).then(res => {
          if (res.code === 0) { this.$message.success('删除成功'); this.fetchGroups() }
        })
      }).catch(() => {})
    },
    toggleStatus(row) {
      customerGroupStatus(row.id, row.isDelete === 1 ? 2 : 1).then(res => {
        if (res.code === 0) { this.$message.success('状态更新成功'); this.fetchGroups() }
      })
    },
    permTypeLabel(t) { return PERM_TYPE_LABELS[t] || t },
    /** 该权限行的 KYC 下拉是否锁定（开立真实账户固定要求身份认证，只能切启用状态） */
    isKycLocked(p) { return !!p && LOCKED_KYC_PERM_TYPES.indexOf(p.permType) !== -1 },
    /**
     * 规整权限行（弹窗展示与保存都走这里）：
     * ① kycLevel 统一成数组，并过滤掉下拉框已下线的 1「添加」；
     * ② 补齐缺失的权限类型（历史客户组没有 perm_type=5，不补齐就永远配不到新权限）；
     * ③ 锁定类型强制为身份认证；
     * ④ 按 sort 排序。
     * 补齐出来的行没有 id，保存时后端按 permType upsert 新建。
     */
    normalizePermissions(rows) {
      const list = (rows || []).map(r => {
        let levels = r.kycLevel
        if (levels == null || levels === '') {
          levels = []
        } else if (typeof levels === 'string') {
          levels = levels.split(',').map(Number)
        } else {
          levels = levels.slice()
        }
        return { ...r, kycLevel: levels.filter(c => KYC_OPTIONS.some(k => k.code === c)) }
      })
      DEFAULT_PERMS.forEach(d => {
        if (!list.some(r => r.permType === d.permType)) {
          list.push({ permType: d.permType, kycLevel: d.kycLevel.slice(), isDelete: d.isDelete, sort: d.sort })
        }
      })
      list.forEach(r => {
        if (LOCKED_KYC_PERM_TYPES.indexOf(r.permType) !== -1) {
          r.kycLevel = LOCKED_KYC_LEVEL.slice()
        }
      })
      return list.sort((a, b) => (a.sort || 0) - (b.sort || 0))
    }
  }
}
</script>

<style scoped>
.cg-page {
  padding: 16px;
  background: #f5f7fa;
  min-height: 100%;
  overflow-x: hidden;
}

.info-bar {
  margin-bottom: 12px;
  padding: 8px 14px;
  background: #f7fbff;
  border-radius: 8px;
  font-size: 12px;
  color: #7c8aa5;
  border: 1px solid #edf3fb;
}

.cg-layout {
  display: flex;
  gap: 16px;
  min-height: 620px;
  min-width: 0;
  flex-wrap: nowrap;
  align-items: stretch;
}

/* ==================== 左侧国家列表 ==================== */
.cg-sidebar {
  flex: 0 0 220px;
  width: 220px;
  min-width: 220px;
  max-width: 100%;
  background: #fff;
  border-radius: 4px;
  padding: 12px;
}

.sidebar-add-area {
  margin-bottom: 10px;
}

.select-with-close {
  display: flex;
  align-items: center;
  gap: 4px;
}

.close-select-btn {
  font-size: 16px;
  color: #909399;
  cursor: pointer;
  padding: 4px;
  border-radius: 2px;
  transition: color 0.2s;
}

.close-select-btn:hover {
  color: #f56c6c;
}

.country-list {
  margin-top: 4px;
}

.country-item {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 10px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 13px;
  color: #333;
  margin-bottom: 2px;
  transition: background 0.15s;
}

.country-item:hover {
  background: #f0f2f5;
}

.country-item.active {
  background: var(--brand-primary-light);
  color: var(--brand-primary);
  font-weight: 500;
}

.country-icon {
  font-size: 14px;
  width: 20px;
  text-align: center;
  flex-shrink: 0;
}

.country-name {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.country-delete {
  font-size: 14px;
  color: #c0c4cc;
  padding: 0 4px;
  transition: color 0.2s;
}

.country-delete:hover {
  color: #f56c6c;
}

/* ==================== 右侧内容区 ==================== */
.cg-content {
  flex: 1 1 auto;
  width: 0;
  min-width: 0;
  max-width: 100%;
  background: #fff;
  border-radius: 4px;
  padding: 20px 24px 22px;
  border: 1px solid #e4e7ed;
}

.content-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  padding-bottom: 14px;
  border-bottom: 1px solid #ebeef5;
}

.content-title-row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.content-title {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
}

.country-type-tag {
  border-color: var(--brand-primary);
  color: var(--brand-primary);
  background: var(--brand-primary-light);
}

.table-wrapper {
  background: #fff;
  border-radius: 4px;
}

.table-header {
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.table-title {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
}

/* 「设为默认」列只有一个单选圆点：el-radio 默认带 margin-right:30px、label 带 padding-left:10px，
   单元格 align=center 时会整体偏左，这里去掉才能正中。 */
::v-deep .cg-table .default-radio.el-radio {
  margin-right: 0;
}

::v-deep .cg-table .default-radio .el-radio__label {
  display: none;
}

.mono {
  font-family: monospace;
  font-size: 12px;
}

.cell-muted {
  color: #909399;
  font-size: 12px;
}

.empty-tip {
  padding: 40px;
  text-align: center;
  color: #909399;
}
</style>
